import axios, { CancelTokenSource } from 'axios';
import React from 'react';
import _ from 'underscore';

import {
  DxfModel,
  FileModel,
  GlbModel,
} from '../../../../../../typings/api/skymap/rest/v1/.common';
import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { iiafe } from '../../../../utils/async';
import { findLastFileVersion } from '../../../../utils/models/file-version';
import { isDefined } from '../../../../utils/variables';
import { ProjectContext } from '../../../state/project-state/project-state';
import { TreeContext } from '../state/folder-tree-state';

const timeoutMap: { [totalMinutes: number]: number } = {
  1: 5, // First minute, each 5 seconds.
  5: 10, // After 1 minute, each 10 seconds.
  10: 30, // After 5 minutes, each 30 seconds.
  30: 60, // After 10 minutes, each 60 seconds.
  // Abort after 30 minutes.
};

export type FileVersionCadStatus = {
  fileId: string;
  dxf?: DxfModel;
  glb?: GlbModel;
};

/**
 * Polls files from API and checkes whether CAD processing status of last file version has changed
 * from RUNNING to another status. On each poll, info about any file version changed are notified
 * to caller via the onChange callback.
 *
 * @param files A list of files with file versions. Only files with status RUNNING are checked.
 * @param onChange Callback with information of new file version CAD processing status.
 */
export const useCheckCadStatus = (
  files: FileModel[],
  onChange: (fileVersionsChanged: FileVersionCadStatus[]) => void,
) => {
  const { project, sharedFolderId } = React.useContext(ProjectContext);
  const { selectedFolderId } = React.useContext(TreeContext);

  const [startTime, setStartTime] = React.useState(new Date().getTime());
  const cancelTokenSource = React.useRef(axios.CancelToken.source());
  const timeoutId = React.useRef<NodeJS.Timeout>();
  const fileIdsInProgress = React.useRef<string[]>([]);

  /**
   * Returns a list of IDs represening the files that has a latest version in progress.
   */
  const getFileIdsInProgress = (files: FileModel[]) =>
    files
      .filter((x) => {
        const lastFileVersion = findLastFileVersion(x);
        return lastFileVersion.dxf
          ? lastFileVersion.dxf.status === 'RUNNING'
          : lastFileVersion.glb
            ? lastFileVersion.glb.status === 'RUNNING'
            : false;
      })
      .map((x) => x.id);

  /**
   * Returns the number of ms to wait until next API poll.
   */
  const calcTimeoutDelay = React.useCallback((): number | null => {
    const totalSeconds = (new Date().getTime() - startTime) / 1000;

    for (const [minutes, secondsToWait] of Object.entries(timeoutMap)) {
      if (totalSeconds < parseInt(minutes, 10) * 60) {
        return secondsToWait * 1000;
      }
    }

    return null;
  }, [startTime]);

  /**
   * Returns a promise to fetch files either from root folder or current selected folder.
   */
  const fetchFiles = React.useCallback(
    (cancelTokenSource: CancelTokenSource) =>
      selectedFolderId
        ? SkyMapAxiosServiceFactory.instance.createFolderServiceV1().getSubFolderFiles(
            {
              path: {
                folderId: selectedFolderId,
              },
              query: {
                include: ['file.version.dxf', 'file.version.glb'],
              },
            },
            cancelTokenSource.token,
          )
        : SkyMapAxiosServiceFactory.instance.createProjectServiceV1().getRootFolderFiles(
            {
              path: {
                projectId: project.id,
              },
              query: {
                include: ['file.version.dxf', 'file.version.glb'],
              },
            },
            cancelTokenSource.token,
          ),
    [project.id, selectedFolderId],
  );

  /**
   * Sets up a timeout that periodically polls the API to check for CAD status changes.
   */
  const setupPoll = React.useCallback(() => {
    async function check() {
      try {
        // Fetch files.
        cancelTokenSource.current = axios.CancelToken.source();
        const apiFiles = await fetchFiles(cancelTokenSource.current);

        // Get ID of files in progress from API response.
        // Also get IDS of files no longer in progress (or wan't found in API response).
        const updatedFileIdsInProgress = getFileIdsInProgress(apiFiles.data);
        const changedFileIds = _.difference(fileIdsInProgress.current, updatedFileIdsInProgress);
        fileIdsInProgress.current = updatedFileIdsInProgress;

        // Get last version of each file changed (skip any deleted files).
        const fileVersionsChanged = changedFileIds
          .map((fileId) => apiFiles.data.find((x) => x.id === fileId))
          .filter(isDefined)
          .map((file) => {
            const lastFileVersion = findLastFileVersion(file);
            return {
              fileId: file.id,
              dxf: lastFileVersion.dxf,
              glb: lastFileVersion.glb,
            };
          });

        // Notify changes.
        if (fileVersionsChanged.length > 0) {
          onChange(fileVersionsChanged);
        }

        // Continue to poll API for changes as long as there are files in progress.
        if (fileIdsInProgress.current.length > 0) {
          setupTimeout();
        }
      } catch (err) {
        // Do nothing on error, as this is a behind the scene check only.
      }
    }

    /**
     * Sets up timeout before polling API. Or stops polling if we have waited too long.
     */
    function setupTimeout() {
      const timeout = calcTimeoutDelay();
      if (isDefined(timeout)) {
        timeoutId.current = setTimeout(() => iiafe(check), timeout);
      }
    }

    setupTimeout();
  }, [calcTimeoutDelay, fetchFiles, onChange]);

  /**
   * If any new files has been uploaded, reset start time in order to reset to a smaller poll
   * interval.
   */
  React.useEffect(() => {
    const fileIds = getFileIdsInProgress(files);

    if (_.difference(fileIds, fileIdsInProgress.current).length > 0) {
      setStartTime(new Date().getTime());
    }

    fileIdsInProgress.current = fileIds;
  }, [files]);

  /**
   * Setup a new timeout whenever a depencendy has changed.
   * On cleanup, clear current timeout and network request.
   */
  React.useEffect(() => {
    if (!isDefined(sharedFolderId) && fileIdsInProgress.current.length > 0) {
      setupPoll();
    }

    return () => {
      clearTimeout(timeoutId.current);
      cancelTokenSource.current.cancel();
    };
  }, [setupPoll, sharedFolderId]);
};
